// Панель владельца кампании — открывается из шестерёнки в шапке, только для
// организатора (setup.canManage). Дебаг/бета-раздел показывается лишь платформенному
// администратору (setup.isPlatformAdmin — учётка из env). Изолированная .gn-* разметка;
// вся логика — реальные методы api, мутации через action() (он перезагружает setup).

import { useState } from "react";
import {
  api,
  type BetaAdminDiagnostics,
  type CampaignSetup,
} from "../api";
import { forcePhaseHelp, phaseInfo, initials } from "../cycle/shared";
import { CycleTimingCard } from "./CycleTimingCard";

type GnAction = (label: string, run: () => Promise<unknown>) => Promise<boolean>;

function WarnIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    </svg>
  );
}
function WrenchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.5-.7-.7-2.5z" />
    </svg>
  );
}
function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
      <path d="M21 3v5h-5" />
    </svg>
  );
}
function FactsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7V4h16v3M9 20h6M12 4v16" />
    </svg>
  );
}
function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
    </svg>
  );
}
function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2 2 7v6c0 5 3.5 8 10 9 6.5-1 10-4 10-9V7z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

const MAX_PLAYERS = 10;

export function OwnerPanel({
  campaignId,
  setup,
  action,
  onClose,
}: {
  campaignId: string;
  setup: CampaignSetup;
  action: GnAction;
  onClose: () => void;
}) {
  const campaign = setup.campaign;
  const overview = setup.cycle;
  const cycle = overview.current;
  const isAdmin = setup.isPlatformAdmin;

  const [confirmForce, setConfirmForce] = useState(false);
  const [invite, setInvite] = useState("");
  const [diag, setDiag] = useState<BetaAdminDiagnostics | null>(null);

  const forceLabel = cycle?.forceCompletePhaseLabel;
  const canForce = !!(cycle?.canForceCompletePhase && forceLabel);
  const atCap = campaign.memberCount >= MAX_PLAYERS;

  async function sendInvite() {
    const target = invite.trim();
    if (!target) return;
    const ok = await action("Отправляем приглашение…", () => api.inviteCampaignUser(campaignId, target));
    if (ok) setInvite("");
  }
  async function refreshDiag() {
    const r = await api.betaDiagnostics(campaignId).catch(() => null);
    if (r) setDiag(r);
  }
  async function downloadExport() {
    const link = await api.betaDebugExportLink(campaignId).catch(() => null);
    if (link?.downloadUrl) window.open(link.downloadUrl, "_blank", "noopener");
  }

  return (
    <div className="gn-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="gn-modal gn-owner" onClick={(e) => e.stopPropagation()}>
        <div className="gn-modal-head">
          <div className="gn-tt">
            <b>Настройки владельца</b>
            <span>Только организатор</span>
          </div>
          <button type="button" className="gn-modal-x" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>

        {/* A. Управление этапом */}
        <div className="gn-panel gn-osec">
          <span className="gn-eyebrow">Управление этапом</span>
          {cycle && (
            <span className="gn-phasepill">
              <i />
              Сейчас · {phaseInfo(cycle.phase).label}
            </span>
          )}
          {canForce && cycle && (
            <div className="gn-dcard">
              <h3>{forceLabel}</h3>
              <div className="gn-warnrow">
                <WarnIcon /> Разрушительно · требует подтверждения
              </div>
              <p>{forcePhaseHelp(cycle.phase)}</p>
              <button type="button" className="gn-dbtn" onClick={() => setConfirmForce(true)}>
                {forceLabel}
              </button>
            </div>
          )}
          {overview.canStartCycle && (
            <button type="button" className="gn-cta" onClick={() => void action("Запускаем цикл…", () => api.startCycle(campaignId))}>
              Начать цикл
            </button>
          )}
          {!canForce && !overview.canStartCycle && (
            <p className="gn-omuted">Сейчас нет ручных действий по этапу — цикл идёт по таймингам.</p>
          )}
        </div>

        {/* B. Игроки */}
        <div className="gn-panel gn-osec">
          <span className="gn-ocount">
            {campaign.memberCount} / {MAX_PLAYERS}
          </span>
          <span className="gn-eyebrow">Игроки</span>
          <div className="gn-invrow">
            <input
              className="gn-oinput"
              placeholder="@username или ID"
              value={invite}
              onChange={(e) => setInvite(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void sendInvite();
              }}
            />
            <button type="button" className="gn-invbtn" disabled={!invite.trim() || atCap} onClick={() => void sendInvite()}>
              Пригласить
            </button>
          </div>
          {atCap && <p className="gn-omuted">Достигнут предел в {MAX_PLAYERS} участников.</p>}
          {campaign.inviteCode && (
            <div className="gn-code">
              <div className="gn-cl">
                <small>Код приглашения</small>
                <b>{campaign.inviteCode}</b>
              </div>
              <button
                type="button"
                className="gn-darkbtn gn-cp"
                onClick={() => void navigator.clipboard?.writeText(campaign.inviteCode ?? "")}
              >
                Скопировать
              </button>
            </div>
          )}
          {setup.pendingInvitations.length > 0 && (
            <div className="gn-pend">
              <div className="gn-pt">Ожидают ответа · {setup.pendingInvitations.length}</div>
              {setup.pendingInvitations.map((inv) => (
                <div className="gn-prow" key={inv.id}>
                  <span className="gn-pav">{initials(inv.invitedUserDisplayName)}</span>
                  <span className="gn-pn">
                    {inv.invitedUserDisplayName}
                    {inv.invitedUserUsername ? ` · @${inv.invitedUserUsername}` : ""}
                  </span>
                  <span className="gn-ps">приглашён</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* C. Настройки кампании */}
        <div className="gn-panel gn-osec">
          <span className="gn-eyebrow">Настройки кампании</span>
          <label className={`gn-switch${campaign.publicVisible ? " on" : ""}`}>
            <input
              type="checkbox"
              checked={campaign.publicVisible}
              onChange={(e) =>
                void action(
                  e.target.checked ? "Открываем в поиске…" : "Скрываем из поиска…",
                  () => api.updateCampaignVisibility(campaignId, e.target.checked),
                )
              }
            />
            <span className="gn-sw" />
            <span>
              <b>Показывать в публичном поиске</b>
              <small>Кампанию смогут находить и запрашивать вступление другие игроки.</small>
            </span>
          </label>
          <CycleTimingCard campaignId={campaignId} value={overview.timingSettings} action={action} />
        </div>

        {/* D. Диагностика беты — только платформенному админу */}
        {isAdmin && (
          <div className="gn-panel gn-osec gn-devsec">
            <div className="gn-devtag">
              <WrenchIcon />
              <div>
                <b>Только для разработчика</b>
                <small>Виден лишь вашей учётной записи (из env), другим владельцам — нет.</small>
              </div>
            </div>
            <span className="gn-eyebrow gn-eyebrow-p">Диагностика беты</span>
            <div className="gn-betahead">
              <div className="gn-rl">
                <b>Готовность к игре</b>
                <div className="gn-rbar">
                  <i style={{ width: `${Math.max(0, Math.min(100, setup.betaReadiness.score))}%` }} />
                </div>
              </div>
              <span className="gn-rv">
                {setup.betaReadiness.score}% · {setup.betaReadiness.statusLabel}
              </span>
            </div>
            {diag && <p className="gn-omuted">{diag.summary}</p>}
            <div className="gn-betabtns">
              <button type="button" className="gn-darkbtn gn-bb" onClick={() => void refreshDiag()}>
                <RefreshIcon />
                <span>
                  <b>Обновить диагностику</b>
                  <small>
                    {diag?.currentCycle
                      ? `Цикл ${diag.currentCycle.cycleNumber} · ${diag.currentCycle.phase} · действий ${diag.currentCycle.actionsTotal}`
                      : "текущий цикл, счётчики действий"}
                  </small>
                </span>
              </button>
              <button
                type="button"
                className="gn-darkbtn gn-bb"
                onClick={() => void action("Создаём факты цикла…", () => api.betaBackfillCurrentCycleFacts(campaignId))}
              >
                <FactsIcon />
                <span>
                  <b>Создать факты текущего цикла</b>
                  <small>ремонт реестра фактов</small>
                </span>
              </button>
            </div>
            <div className="gn-exp">
              <div className="gn-eh">
                <ShieldIcon />
                <b>Скачать debug-export</b>
              </div>
              <p>
                Полный приватный слепок кампании (скрытые цели, мотивы, брифинги). Только для отладки —{" "}
                <b>не отправляйте игрокам целиком</b>.
              </p>
              <button type="button" className="gn-darkbtn gn-bb" onClick={() => void downloadExport()}>
                <DownloadIcon />
                <span>
                  <b>Скачать слепок</b>
                  <small>JSON · подписанная ссылка</small>
                </span>
              </button>
            </div>
          </div>
        )}
        <div style={{ height: "8px" }} />
      </div>

      {confirmForce && cycle && (
        <div className="gn-modal-backdrop gn-confirm-back" onClick={() => setConfirmForce(false)}>
          <div className="gn-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="gn-ci">
              <WarnIcon />
            </div>
            <h3>{forceLabel}?</h3>
            <p>{forcePhaseHelp(cycle.phase)} Отменить нельзя.</p>
            <div className="gn-cbtns">
              <button type="button" className="gn-darkbtn gn-ccancel" onClick={() => setConfirmForce(false)}>
                Отмена
              </button>
              <button
                type="button"
                className="gn-cgo"
                onClick={() => {
                  setConfirmForce(false);
                  void action("Завершаем этап…", () => api.forceCompleteCyclePhase(campaignId));
                }}
              >
                {forceLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
